import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import SmileSignalBadge from './SmileSignalBadge';

// ─────────────────────────────────────────────
// Pending approvals section (force-release + successor verifications)
// ─────────────────────────────────────────────
export function PendingApprovalsSection({
  pendingFR, pendingVerifs, pendingTab, setPendingTab,
  onApproveFR, onRejectFR, onApproveVerif, onRejectVerif,
}) {
  const hasItems = pendingFR.length > 0 || pendingVerifs.length > 0;
  return (
    <div
      className={`bg-gray-800 rounded-xl p-4 border ${hasItems ? 'border-rose-600/70' : 'border-gray-700'}`}
      data-testid="ev-pending-approvals"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-white font-semibold flex items-center gap-2">
          {hasItems ? <AlertTriangle className="w-4 h-4 text-rose-400" /> : <ShieldCheck className="w-4 h-4 text-emerald-400" />}
          Pending approvals
          <span className="text-xs text-gray-400 font-normal">({pendingFR.length + pendingVerifs.length})</span>
        </h3>
        <div className="inline-flex rounded-md border border-gray-600 overflow-hidden text-xs" data-testid="ev-pending-tabs">
          <button
            type="button"
            onClick={() => setPendingTab('force-release')}
            className={`px-3 py-1 ${pendingTab === 'force-release' ? (hasItems ? 'bg-rose-600 text-white' : 'bg-emerald-700 text-white') : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
            data-testid="ev-pending-tab-fr"
          >
            Force-release ({pendingFR.length})
          </button>
          <button
            type="button"
            onClick={() => setPendingTab('verifications')}
            className={`px-3 py-1 ${pendingTab === 'verifications' ? (hasItems ? 'bg-rose-600 text-white' : 'bg-emerald-700 text-white') : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
            data-testid="ev-pending-tab-verif"
          >
            Trusted Contact verifications ({pendingVerifs.length})
          </button>
        </div>
      </div>

      {pendingTab === 'force-release' ? (
        pendingFR.length === 0 ? (
          <div className="text-center py-6" data-testid="ev-empty-fr">
            <ShieldCheck className="w-8 h-8 text-emerald-500/70 mx-auto mb-2" />
            <p className="text-sm text-gray-300">No force-release requests pending</p>
            <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
              When an admin opens a force-release via the Manual override panel, a second admin will be asked to approve here before any Continuity System is released.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-2">A second admin (not the initiator) must approve before any Continuity System is released.</p>
            <div className="space-y-2" data-testid="ev-pending-fr">
              {pendingFR.map((r) => (
                <div key={r.request_id} className="border border-gray-700 rounded p-2" data-testid={`ev-fr-${r.request_id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="text-sm text-gray-200 min-w-0">
                      <div className="font-mono text-xs">{r.vault_id}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Initiated by <span className="text-gray-200">{(r.first_approver || {}).admin_id}</span> &middot; expires {r.expires_at?.slice(0, 16)}
                      </div>
                      <div className="text-xs text-gray-300 mt-1 italic">&ldquo;{(r.first_approver || {}).justification}&rdquo;</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={() => onApproveFR(r)} className="bg-rose-600 hover:bg-rose-700 text-xs h-7" data-testid={`ev-fr-approve-${r.request_id}`}>Approve &amp; release</Button>
                      <Button size="sm" variant="outline" onClick={() => onRejectFR(r)} className="text-gray-300 border-gray-500 text-xs h-7" data-testid={`ev-fr-reject-${r.request_id}`}>Reject</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      ) : (
        pendingVerifs.length === 0 ? (
          <div className="text-center py-6" data-testid="ev-empty-verif">
            <ShieldCheck className="w-8 h-8 text-emerald-500/70 mx-auto mb-2" />
            <p className="text-sm text-gray-300">No Trusted Contact verifications pending</p>
            <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
              When a Trusted Contact uploads their four verification documents, they will appear here for two-admin review.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {pendingVerifs.map((v) => (
              <div key={v.verification_id} className="border border-gray-700 rounded p-2 flex items-center justify-between gap-2" data-testid={`ev-verif-${v.verification_id}`}>
                <div className="text-sm text-gray-200 min-w-0 flex-1">
                  <div>Trusted Contact {v.successor_id} &middot; Continuity System {v.vault_id}</div>
                  <div className="text-xs text-gray-400">{v.doc_type || 'document'} &middot; Approvals: {(v.approvals || []).length} of 2 &middot; uploaded {v.uploaded_at}</div>
                  <SmileSignalBadge signal={v.smile_signal} />
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => onApproveVerif(v.verification_id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`ev-verif-approve-${v.verification_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => onRejectVerif(v.verification_id)} className="text-rose-400 border-rose-400" data-testid={`ev-verif-reject-${v.verification_id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Begin information transfer dialog
// ─────────────────────────────────────────────
export function BeginTransferDialog({
  transferSuccessor, setTransferSuccessor,
  transferJustification, setTransferJustification,
  transferSubmitting, onSubmit,
}) {
  return (
    <AlertDialog open={!!transferSuccessor} onOpenChange={(o) => { if (!o) setTransferSuccessor(null); }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Begin information transfer{transferSuccessor ? ` for ${transferSuccessor.fullName}` : ''}?</AlertDialogTitle>
          <AlertDialogDescription>
            An encrypted one-time link will be emailed to this person. They have 24 hours to upload their verification documents. Two of our staff must then independently approve before any information is shared. This step is permanently audit-logged.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mt-2">
          <Label htmlFor="ev-transfer-just" className="text-sm">Why are you beginning this transfer?</Label>
          <Textarea
            id="ev-transfer-just" rows={4}
            value={transferJustification}
            onChange={(e) => setTransferJustification(e.target.value.slice(0, 600))}
            placeholder="e.g. Family member reported owner's passing by phone on 5 Feb. Spoken with Mary Davis (daughter)."
            data-testid="ev-transfer-justification"
          />
          <p className="text-xs text-slate-500 mt-1">
            {transferJustification.length}/600 characters &middot; minimum 8 required
          </p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ev-transfer-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onSubmit(); }}
            disabled={transferSubmitting || transferJustification.trim().length < 8}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="ev-transfer-confirm"
          >
            {transferSubmitting ? 'Sending…' : 'Send secure link'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─────────────────────────────────────────────
// Manual override dialog
// ─────────────────────────────────────────────
export function OverrideDialog({
  overrideDialogOpen, setOverrideDialogOpen,
  overrideAction, setOverrideAction,
  overrideJustification, setOverrideJustification,
  overrideDays, setOverrideDays,
  overrideSubmitting, onRun,
}) {
  return (
    <AlertDialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Manual override</AlertDialogTitle>
          <AlertDialogDescription>
            Use this only when normal flows cannot resolve the situation. Every action below is permanently audit-logged with your justification.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="ev-override-action-sel" className="text-sm">Action</Label>
            <select
              id="ev-override-action-sel"
              value={overrideAction}
              onChange={(e) => setOverrideAction(e.target.value)}
              className="mt-1 w-full bg-white border border-slate-300 rounded px-2 py-2 text-sm"
              data-testid="ev-override-action"
            >
              <option value="">Choose an action…</option>
              <option value="extend_checkin">Extend the next check-in</option>
              <option value="force_release">Force-release (after legal review)</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Pause, resume and turn-off are not available here &mdash; those must be performed by the owner from their Settings.
            </p>
          </div>

          {overrideAction === 'extend_checkin' && (
            <div>
              <Label htmlFor="ev-override-days-input" className="text-sm">Extend by (days)</Label>
              <Input
                id="ev-override-days-input"
                type="number" min="1" max="180"
                value={overrideDays}
                onChange={(e) => setOverrideDays(e.target.value)}
                className="mt-1"
                data-testid="ev-override-days"
              />
            </div>
          )}

          <div>
            <Label htmlFor="ev-override-just" className="text-sm">Justification</Label>
            <Textarea
              id="ev-override-just" rows={3}
              value={overrideJustification}
              onChange={(e) => setOverrideJustification(e.target.value.slice(0, 600))}
              placeholder="Explain why this override is necessary (8+ chars, audit-logged)…"
              className="mt-1"
              data-testid="ev-override-justification"
            />
            <p className="text-xs text-slate-500 mt-1">
              {overrideJustification.length}/600 characters &middot; minimum 8 required
            </p>
          </div>

          {overrideAction === 'force_release' && (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              Force-release does <strong>not</strong> release the Continuity System immediately. It opens a pending request that a different admin must independently approve within 72 hours before any information is shared. Use only after legal review.
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ev-override-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onRun(); }}
            disabled={overrideSubmitting || !overrideAction || overrideJustification.trim().length < 8}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="ev-override-run"
          >
            {overrideSubmitting ? 'Applying…' : 'Apply override'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─────────────────────────────────────────────
// Force-release approve / reject dialog
// ─────────────────────────────────────────────
export function ForceReleaseDecisionDialog({
  frDialog, setFrDialog,
  frJustification, setFrJustification,
  frSubmitting, onSubmit,
}) {
  return (
    <AlertDialog open={!!frDialog} onOpenChange={(o) => { if (!o) { setFrDialog(null); setFrJustification(''); } }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            {frDialog?.mode === 'approve' ? 'Approve force-release?' : 'Reject force-release?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {frDialog?.mode === 'approve'
              ? 'Approving will release the Continuity Records to the named Trusted Contacts immediately. This action is permanent and audit-logged.'
              : 'Rejecting will cancel this pending force-release request. The Continuity System will remain unchanged.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {frDialog && (
          <div className="space-y-3 mt-2">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700">
              <div><span className="text-slate-500">Vault:</span> <span className="font-mono">{frDialog.request.vault_id}</span></div>
              <div className="mt-1"><span className="text-slate-500">Initiated by:</span> {(frDialog.request.first_approver || {}).admin_id}</div>
              <div className="mt-1 italic">&ldquo;{(frDialog.request.first_approver || {}).justification}&rdquo;</div>
            </div>

            <div>
              <Label htmlFor="ev-fr-just" className="text-sm">
                {frDialog.mode === 'approve' ? 'Your independent justification' : 'Reason for rejection'}
              </Label>
              <Textarea
                id="ev-fr-just" rows={3}
                value={frJustification}
                onChange={(e) => setFrJustification(e.target.value.slice(0, 600))}
                placeholder={frDialog.mode === 'approve'
                  ? 'e.g. Independently reviewed solicitor evidence on 6 Feb. Endorse release.'
                  : 'e.g. Evidence is insufficient; need death certificate before approving.'}
                className="mt-1"
                data-testid="ev-fr-decision-justification"
              />
              <p className="text-xs text-slate-500 mt-1">
                {frJustification.length}/600 characters &middot; minimum 8 required
              </p>
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ev-fr-decision-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onSubmit(); }}
            disabled={frSubmitting || frJustification.trim().length < 8}
            className={frDialog?.mode === 'approve' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-slate-700 hover:bg-slate-800'}
            data-testid="ev-fr-decision-confirm"
          >
            {frSubmitting
              ? (frDialog?.mode === 'approve' ? 'Releasing…' : 'Rejecting…')
              : (frDialog?.mode === 'approve' ? 'Approve & release' : 'Confirm rejection')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
